/**
 * @param {string} type - The type of the response to be returned. Default is 'json'.
 * @returns {Object} - The configuration object for the fetch request.
 */
function fetchConfig(type = 'json') {
  return {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: `application/${type}` },
  };
}

/**
 * Fetch HTML
 * @param {string} text - The text to parse
 * @returns {Document} The parsed HTML document
 */
function parseHTML(text) {
  return new DOMParser().parseFromString(text, 'text/html');
}

/**
 * Fetch HTML
 * @param {string} url - The URL to fetch
 * @param {RequestInit} [init] - The init to pass to the fetch function
 * @returns {Promise<Document>} The HTML document
 */
async function fetchHTML(url, init = {}) {
  const response = await fetch(`${url}`, init);
  return parseHTML(await response.text());
}

function getElementDimensions(element = null) {
  if (!element) return;

  const elementDom = document.querySelector(element);
  const rect = elementDom.getBoundingClientRect();
  return rect;
}

/**
 * Get all focusable elements inside a container
 * @param {HTMLElement} container
 * @returns {HTMLElement[]}
 */
function getFocusableElements(container) {
  if (!container?.querySelectorAll) return [];

  try {
    return Array.from(
      container.querySelectorAll(
        "summary, a[href], button:enabled, [tabindex]:not([tabindex^='-']), [draggable], area, input:not([type=hidden]):enabled, select:enabled, textarea:enabled, object, iframe"
      )
    ).filter((element) => {
      try {
        for (let current = element; current; current = current.parentElement) {
          const style = getComputedStyle(current);
          if (style.display === 'none' || style.visibility === 'hidden') return false;
        }
        return true;
      } catch {
        return false;
      }
    });
  } catch {
    return [];
  }
}

/**
 * Get the index of a node in its parent
 * @param {HTMLElement} node
 * @returns {number}
 */
function getNodeIndex(node) {
  return Array.from(node.parentNode.children).indexOf(node);
}

/**
 * Detects a click outside of a given element
 * @param {HTMLElement} element - The element to detect a click outside of
 * @param {Function} callback - The callback to execute when a click is detected outside of the element
 * @returns {Function} - A function to remove the event listener
 * @example
 * this.drawer = this.querySelector('drawer');
 * onClickOutside(this.drawer, () => this.close());
 */
function onClickOutside(element, callback) {
  const handleClick = (event) => {
    if (element && !element.contains(event.target)) callback();
  };

  document.addEventListener('mousedown', handleClick);

  return () => {
    document.removeEventListener('mousedown', handleClick);
  };
}

//#region src/string/capitalize.ts
/**
* Converts the first character of string to upper case and the remaining to lower case.
*
* @template T - Literal type of the string.
* @param str - The string to be converted to uppercase.
* @returns The capitalized string.
*
* @example
* const result = capitalize('fred') // returns 'Fred'
* const result2 = capitalize('FRED') // returns 'Fred'
*/
function capitalize(str) {
	return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
}

//#region src/string/words.ts
/**
* Regular expression pattern to split strings into words for various case conversions
*
* This pattern matches sequences of characters in a string, considering the following cases:
* - Sequences of two or more uppercase letters followed by an uppercase letter and lowercase letters or digits (for acronyms)
* - Sequences of one uppercase letter optionally followed by lowercase letters and digits
* - Single uppercase letters
* - Sequences of digits
* - Emojis and other Unicode characters
*
* The resulting match can be used to convert camelCase, snake_case, kebab-case, and other mixed formats into
* a consistent format like snake case. It also supports emojis and other Unicode characters.
*
* @example
* const matches = 'camelCaseHTTPRequest🚀'.match(CASE_SPLIT_PATTERN);
* // matches: ['camel', 'Case', 'HTTP', 'Request', '🚀']
*/
const CASE_SPLIT_PATTERN = /\p{Lu}?\p{Ll}+|[0-9]+|\p{Lu}+(?!\p{Ll})|\p{Emoji_Presentation}|\p{Extended_Pictographic}|\p{L}+/gu;
/**
* Splits `string` into an array of its words, treating spaces and punctuation marks as separators.
*
* @param str The string to inspect.
* @param [pattern] The pattern to match words.
* @returns Returns the words of `string`.
*
* @example
* words('fred, barney, & pebbles');
* // => ['fred', 'barney', 'pebbles']
*
* words('camelCaseHTTPRequest🚀');
* // => ['camel', 'Case', 'HTTP', 'Request', '🚀']
*
* words('Lunedì 18 Set')
* // => ['Lunedì', '18', 'Set']
*/
function words(str) {
	return Array.from(str.match(CASE_SPLIT_PATTERN) ?? []);
}

//#region src/string/camelCase.ts
/**
* Converts a string to camel case.
*
* Camel case is the naming convention in which the first word is written in lowercase and
* each subsequent word begins with a capital letter, concatenated without any separator characters.
*
* @param str - The string that is to be changed to camel case.
* @returns The converted string to camel case.
*
* @example
* const convertedStr1 = camelCase('camelCase') // returns 'camelCase'
* const convertedStr2 = camelCase('some whitespace') // returns 'someWhitespace'
* const convertedStr3 = camelCase('hyphen-text') // returns 'hyphenText'
* const convertedStr4 = camelCase('HTTPRequest') // returns 'httpRequest'
* const convertedStr5 = camelCase('Keep unicode 😅') // returns 'keepUnicode😅'
*/
function camelCase(str) {
	const words$1 = words(str);
	if (words$1.length === 0) return "";
	const [first, ...rest] = words$1;
	return `${first.toLowerCase()}${rest.map((word) => capitalize(word)).join("")}`;
}

/**
 * Toggle attribute boolean
 * @param {HTMLElement} element - The element to toggle the attribute boolean on
 * @param {String} attribute - The attribute to toggle
 * @param {'true' | 'false'} [force] - The force value to set the attribute to
 * @returns {Boolean} - The new value of the attribute
 * @example
 * toggleAttributeBoolean(element, 'ariaExpanded');
 */
const toggleAttributeBoolean = (element, attribute, force) => {
  if (!element || !attribute) return console.error('Element and attribute are required');
  attribute = camelCase(attribute);
  if (!element[attribute]) return console.error(`Attribute "${attribute}" not found on element`);

  const newValue = force || element[attribute] !== 'true';

  element[attribute] = newValue.toString();

  return newValue;
};

/**
 * Get the section id
 * @param {HTMLElement} section - The section to get the id from
 * @returns {string} - The section id
 */
const getSectionId = ({ id = '' }) =>
  id.includes('__') ? id.split('__').pop() : id.replace('shopify-section-', '');

export { fetchConfig, fetchHTML, getElementDimensions, getFocusableElements, getNodeIndex, getSectionId, onClickOutside, parseHTML, toggleAttributeBoolean };
